import React from 'react';
import type { FunnelCounters } from '../../lib/funnel';
import { buildPipelineStages, type PipelineStageId } from '../../lib/pipelineStages';
import { buildStageDetail, type FeedTarget, type StageDestination } from '../../lib/stageDetail';
import type { BandTab } from '../../hooks/useBandRoute';
import type { KomState } from '../../lib/kom';
import PipelineFlow from './PipelineFlow';
import PipelineStageDialog from './PipelineStageDialog';

interface PipelineTabProps {
  counters: FunnelCounters;
  /** Until the first read succeeds the counters are placeholders: the stages show a dash, not a zero. */
  state?: KomState;
  onShowTab?: (tab: BandTab) => void;
  /** Opens the Threat Feed with the list, the state and the type a stage asks for already chosen. */
  onShowInFeed?: (target: FeedTarget) => void;
}

const PipelineTab: React.FC<PipelineTabProps> = ({ counters, state = 'ready', onShowTab, onShowInFeed }) => {
  const stages = React.useMemo(() => buildPipelineStages(counters), [counters]);
  const [openStage, setOpenStage] = React.useState<PipelineStageId | null>(null);
  const detail = React.useMemo(
    () => (openStage ? buildStageDetail(openStage, counters) : null),
    [openStage, counters]
  );

  const follow = (destination: StageDestination) => {
    setOpenStage(null);
    if (destination.kind === 'tab') {
      onShowTab?.(destination.tab);
      return;
    }
    // The dialog gives the focus back to the stage as it closes, and that scrolls the page back to it: go to the feed
    // once it has closed, or the jump is undone.
    window.requestAnimationFrame(() => onShowInFeed?.(destination.target));
  };

  const ready = state === 'ready';
  return (
    <div>
      <h2>
        AI Analysis Pipeline{' '}
        <span className="meta">
          Each stage hands its output to the next{ready ? ' · click a stage to see where its work went' : ''}
        </span>
      </h2>
      <PipelineFlow
        stages={stages}
        label="AI analysis pipeline stages"
        // Until the counters are read there is nothing true to open: the stages are plain blocks with a dash.
        onSelect={ready ? setOpenStage : undefined}
        unread={ready ? undefined : state}
      />
      {detail ? <PipelineStageDialog detail={detail} onClose={() => setOpenStage(null)} onGo={follow} /> : null}
    </div>
  );
};

export default PipelineTab;
